'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Play, ArrowDown, Sparkles, Film, Camera } from 'lucide-react';

export default function HeroSection() {
  const [visible, setVisible] = useState(false);
  const [reelOpen, setReelOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <section
        id="hero"
        className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-20"
        aria-label="NASARC hero section"
      >
        {/* Background layers */}
        <div className="absolute inset-0 bg-studio-950" />
        <div className="absolute inset-0 bg-hero-glow" />

        {/* Cinematic grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(201,168,76,0.6) 1px, transparent 1px),
              linear-gradient(90deg, rgba(201,168,76,0.6) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
          }}
        />

        {/* Floating ambient orbs */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-gold-500/5 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-gold-500/4 blur-[100px] pointer-events-none" />

        {/* Floating badges */}
        <div
          className={`absolute top-32 left-8 md:left-16 xl:left-24 hidden lg:flex items-center gap-2 px-3 py-2 bg-studio-800/80 backdrop-blur-sm border border-studio-600/40 rounded-full text-xs text-studio-200 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: '800ms' }}
        >
          <Camera size={12} className="text-gold-400" />
          On-Site Production
        </div>

        <div
          className={`absolute top-48 right-8 md:right-16 xl:right-24 hidden lg:flex items-center gap-2 px-3 py-2 bg-studio-800/80 backdrop-blur-sm border border-studio-600/40 rounded-full text-xs text-studio-200 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: '1000ms' }}
        >
          <Sparkles size={12} className="text-gold-400" />
          AI-Enhanced
        </div>

        <div
          className={`absolute bottom-40 left-8 md:left-20 xl:left-32 hidden lg:flex items-center gap-2 px-3 py-2 bg-studio-800/80 backdrop-blur-sm border border-studio-600/40 rounded-full text-xs text-studio-200 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: '1200ms' }}
        >
          <Film size={12} className="text-gold-400" />
          Post-Production
        </div>

        {/* Main content */}
        <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-12 2xl:px-16 text-center">
          {/* Agency badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-1.5 bg-gold-500/10 border border-gold-500/25 rounded-full mb-8 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-pulse-gold" />
            <span className="text-gold-300 text-xs font-medium tracking-[0.15em] uppercase">
              Creative Studio · Est. 2019
            </span>
          </div>

          {/* Headline */}
          <h1
            className={`font-display font-extrabold leading-[0.95] tracking-tight mb-6 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '150ms' }}
          >
            <span className="block text-5xl md:text-7xl lg:text-8xl xl:text-9xl text-white">
              We Make
            </span>
            <span className="block text-5xl md:text-7xl lg:text-8xl xl:text-9xl gradient-text-gold mt-1">
              Brands Move.
            </span>
          </h1>

          {/* Sub-headline */}
          <p
            className={`max-w-2xl mx-auto text-lg md:text-xl text-studio-300 leading-relaxed mb-10 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            NASARC is a full-service creative studio delivering cinematic video production,
            precision on-site shooting, and AI-enhanced content that converts attention into revenue.
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '450ms' }}
          >
            <button
              onClick={() => {
                document.querySelector('#work')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="group flex items-center gap-3 px-8 py-4 gradient-gold text-studio-950 font-semibold text-base rounded-xl transition-all duration-200 hover:opacity-90 active:scale-95 shadow-lg shadow-gold-500/20"
            >
              View Our Work
              <ArrowDown size={16} className="group-hover:translate-y-0.5 transition-transform duration-200" />
            </button>

            <button
              onClick={() => setReelOpen(true)}
              className="group flex items-center gap-3 px-8 py-4 bg-transparent border border-studio-600/60 text-white font-medium text-base rounded-xl transition-all duration-200 hover:border-gold-500/50 hover:bg-studio-800/40 active:scale-95"
            >
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-gold-500/20 transition-colors duration-200">
                <Play size={12} className="text-white ml-0.5" fill="currentColor" />
              </div>
              Watch Showreel
            </button>
          </div>

          {/* Trusted by strip */}
          <div
            className={`transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '600ms' }}
          >
            <p className="text-studio-500 text-xs font-medium tracking-[0.15em] uppercase mb-5">
              Trusted by ambitious brands
            </p>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              {['MERIDIAN', 'VAULTEX', 'CRŌSS', 'LUMENA', 'FŌRGE', 'AXIOM']?.map((brand) => (
                <span
                  key={brand}
                  className="font-display font-bold text-sm tracking-[0.2em] text-studio-600 hover:text-studio-400 transition-colors duration-300 cursor-default"
                >
                  {brand}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          className={`absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-700 ${
            visible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: '900ms' }}
        >
          <span className="text-studio-500 text-xs tracking-[0.15em] uppercase">Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-studio-500 to-transparent" />
        </div>
      </section>
      {/* Showreel Modal */}
      {reelOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 animate-fade-in"
          role="dialog"
          aria-modal="true"
          aria-label="Showreel video"
        >
          <div
            className="absolute inset-0 bg-black/90 backdrop-blur-sm"
            onClick={() => setReelOpen(false)}
          />
          <div className="relative z-10 w-full max-w-4xl aspect-video bg-studio-900 rounded-2xl border border-studio-700/60 overflow-hidden shadow-2xl">
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gold-500/20 border border-gold-500/40 flex items-center justify-center">
                <Play size={24} className="text-gold-400 ml-1" fill="currentColor" />
              </div>
              <p className="text-studio-300 text-sm">
                {/* Backend: replace with actual video embed */}
                Showreel video would stream here
              </p>
            </div>
            <button
              onClick={() => setReelOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-lg bg-studio-800/80 text-studio-300 hover:text-white hover:bg-studio-700 transition-colors"
              aria-label="Close showreel"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}